import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { useState, useEffect } from "react";
import { useUser } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Printer, Download, Loader2, CheckCircle, Clock, Circle, FileText, Baby, ShieldCheck } from "lucide-react";

type PracticeLevel = "specialist" | "advanced" | "consultant";
const LEVEL_KEY = "rheumhub_practice_level";

export default function CompetencyExport() {
  const { user } = useUser();
  const { toast } = useToast();
  const [level] = useState<PracticeLevel>(
    () => (localStorage.getItem(LEVEL_KEY) as PracticeLevel) ?? "specialist"
  );
  const [exportData, setExportData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch("/api/competency/portfolio/export");
        if (res.ok) setExportData(await res.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleDownloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const res = await fetch("/api/competency/portfolio/export.pdf");
      if (!res.ok) throw new Error("PDF generation failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `rheumhub-portfolio-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: "Couldn't generate PDF",
        description: "Something went wrong. Try again, or use the print button as a fallback.",
        variant: "destructive",
      });
    } finally {
      setPdfDownloading(false);
    }
  };

  const statusIcon = (status: string) => {
    if (status === "achieved") return <CheckCircle className="h-3.5 w-3.5 text-green-600" />;
    if (status === "in_progress") return <Clock className="h-3.5 w-3.5 text-amber-500" />;
    return <Circle className="h-3.5 w-3.5 text-slate-300" />;
  };

  const levelText = (stmt: any) => {
    if (level === "specialist") return stmt.specialistLevel;
    if (level === "advanced") return stmt.advancedLevel;
    return stmt.consultantLevel;
  };

  return (
    <NurseLayout title="Portfolio Export">
      <div className="space-y-4">
        <div className="flex items-center justify-between print:hidden">
          <Link href="/nurse/competency">
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Portfolio Overview
            </Button>
          </Link>
          <div className="flex gap-2">
            <Button
              onClick={() => window.print()}
              variant="outline"
              className="gap-2"
            >
              <Printer className="h-4 w-4" />
              Print Portfolio
            </Button>
            <Button
              onClick={handleDownloadPdf}
              disabled={pdfDownloading}
              className="bg-teal-700 hover:bg-teal-800 text-white gap-2"
            >
              {pdfDownloading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {pdfDownloading ? "Generating PDF…" : "Download as PDF"}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20 print:hidden">
            <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 space-y-8 print:border-0 print:shadow-none print:p-0">
            {/* Portfolio Header */}
            <div className="border-b border-slate-200 pb-6">
              <h1 className="text-2xl font-bold text-slate-900">RheumHub Competency Portfolio</h1>
              <p className="text-slate-600 mt-1">RCN Rheumatology Competency Framework, Second Edition (December 2024)</p>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-400">Nurse:</span>
                  <span className="ml-2 font-medium text-slate-900">{user?.fullName ?? "—"}</span>
                </div>
                <div>
                  <span className="text-slate-400">Practice Level:</span>
                  <span className="ml-2 font-medium text-slate-900 capitalize">{level} Practitioner</span>
                </div>
                <div>
                  <span className="text-slate-400">Generated:</span>
                  <span className="ml-2 font-medium text-slate-900">{new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
                </div>
                <div>
                  <span className="text-slate-400">Email:</span>
                  <span className="ml-2 font-medium text-slate-900">{user?.primaryEmailAddress?.emailAddress ?? "—"}</span>
                </div>
              </div>
            </div>

            {/* Domains */}
            {exportData?.domains?.map((d: any) => (
              <div key={d.domain.id} className="space-y-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{d.domain.code}</span>
                    <h2 className="font-bold text-slate-900">{d.domain.title}</h2>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{d.domain.description}</p>
                </div>

                {d.statements?.map((item: any) => {
                  const status = item.entry?.status ?? "not_started";
                  return (
                    <div key={item.statement.id} className="border border-slate-200 rounded-xl p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        {statusIcon(status)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono text-xs text-slate-400">{item.statement.code}</span>
                            {item.statement.isPaediatric && (
                              <span className="flex items-center gap-1 text-xs text-purple-600">
                                <Baby className="h-3 w-3" />Paediatric
                              </span>
                            )}
                            <span className={`text-xs font-medium capitalize ${status === "achieved" ? "text-green-600" : status === "in_progress" ? "text-amber-600" : "text-slate-400"}`}>
                              {status.replace("_", " ")}
                            </span>
                            {item.entry?.achievedAt && (
                              <span className="text-xs text-slate-400">
                                · Achieved {new Date(item.entry.achievedAt).toLocaleDateString("en-GB")}
                              </span>
                            )}
                          </div>
                          <p className="text-sm font-semibold text-slate-900">{item.statement.statement}</p>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{levelText(item.statement)}</p>
                        </div>
                      </div>

                      {item.entry?.reflectionText && (
                        <div className="ml-6 bg-teal-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-teal-800 mb-1">Reflection</p>
                          <p className="text-xs text-teal-900 leading-relaxed whitespace-pre-wrap">{item.entry.reflectionText}</p>
                        </div>
                      )}

                      {item.signOff && (
                        <div className="ml-6 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                          <p className="text-xs font-semibold text-emerald-800 mb-1 flex items-center gap-1.5">
                            <ShieldCheck className="h-3 w-3" />
                            Supervisor Sign-Off
                          </p>
                          <p className="text-xs text-emerald-900">
                            <span className="font-medium">{item.signOff.name}</span>
                            {item.signOff.role && <span className="text-emerald-800"> · {item.signOff.role}</span>}
                            {item.signOff.email && <span className="text-emerald-700"> · {item.signOff.email}</span>}
                          </p>
                          <p className="text-xs text-emerald-700 mt-0.5">
                            Signed off {new Date(item.signOff.signedAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                          </p>
                          {item.signOff.notes && (
                            <p className="text-xs text-emerald-900 leading-relaxed mt-1.5 pt-1.5 border-t border-emerald-200 whitespace-pre-wrap">{item.signOff.notes}</p>
                          )}
                        </div>
                      )}

                      {item.entry?.assessorNotes && (
                        <div className="ml-6 bg-slate-50 rounded-lg p-3">
                          <p className="text-xs font-semibold text-slate-700 mb-1">Assessor Notes</p>
                          <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{item.entry.assessorNotes}</p>
                        </div>
                      )}

                      {item.evidence?.length > 0 && (
                        <div className="ml-6">
                          <p className="text-xs font-semibold text-slate-600 mb-1.5 flex items-center gap-1.5">
                            <FileText className="h-3 w-3" />
                            Evidence Files ({item.evidence.length})
                          </p>
                          <ul className="space-y-1">
                            {item.evidence.map((ev: any, i: number) => (
                              <li key={i} className="text-xs text-slate-500">
                                {ev.fileName} · {new Date(ev.uploadedAt).toLocaleDateString("en-GB")}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Footer */}
            <div className="border-t border-slate-200 pt-6 text-xs text-slate-400 space-y-1">
              <p><strong>Attribution:</strong> RCN Rheumatology Competency Framework, Second Edition © Royal College of Nursing 2024 (Publication 011 572). BSR endorsed.</p>
              <p>This portfolio is for personal professional development. Achievement of competencies should be validated by your line manager in accordance with your trust's appraisal process and NMC revalidation requirements.</p>
              <p>Generated by RheumHub · {new Date().toLocaleDateString("en-GB")} · For UK healthcare professionals only.</p>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          nav, aside, header, button { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .pl-64 { padding-left: 0 !important; }
          main { padding: 0 !important; }
          .flex-1 { padding: 0 !important; }
        }
      `}</style>
    </NurseLayout>
  );
}
